import {
  flexRender,
  Header,
  HeaderGroup,
  RowData,
} from "@tanstack/react-table";
import { IconButton } from "@radix-ui/themes";
import { ArrowUpIcon, ArrowDownIcon } from "@radix-ui/react-icons";
import * as ShadcnTable from "@/v2/shared/shadcn-ui/table";
import { useMemo } from "react";
import { TextWithTooltip, Tooltip } from "@/components/shared/tooltip";
import { InfoCircledIcon } from "@radix-ui/react-icons";

type TableHeadProps<RowDataType extends RowData> = Header<RowDataType, unknown>;

function TableHead<RowDataType extends RowData>({
  id,
  colSpan,
  column: { columnDef, getToggleSortingHandler, getCanSort, getIsSorted },
  getSize,
  getContext,
}: TableHeadProps<RowDataType>) {
  const align = useMemo(() => {
    return (columnDef.meta as { align: string } | undefined)?.align ?? "left";
  }, [columnDef.meta]);

  const tooltip = useMemo(() => {
    return (columnDef.meta as { headerTooltip: string } | undefined)
      ?.headerTooltip;
  }, [columnDef.meta]);

  const sortDirection = getIsSorted();

  return (
    <ShadcnTable.TableHead
      key={id}
      colSpan={colSpan}
      onClick={getToggleSortingHandler()}
      style={{
        display: "flex",
        width: `${getSize()}px`,
        justifyContent: align,
        alignItems: "center",
        gap: "4px",
        cursor: getCanSort() ? "pointer" : "default",
      }}
    >
      <TextWithTooltip
        size="2"
        color="gray"
        weight={"medium"}
        truncate
        {...{
          className: getCanSort() ? "cursor-pointer select-none" : "",
        }}
      >
        {flexRender(columnDef.header, getContext())}
      </TextWithTooltip>

      {tooltip != null && (
        <Tooltip>
          <Tooltip.Trigger>
            <IconButton variant="soft" size="1" color="gray">
              <InfoCircledIcon height={16} width={16} />
            </IconButton>
          </Tooltip.Trigger>

          <Tooltip.Content>{tooltip}</Tooltip.Content>
        </Tooltip>
      )}

      {sortDirection === "asc" && <ArrowUpIcon />}
      {sortDirection === "desc" && <ArrowDownIcon />}
    </ShadcnTable.TableHead>
  );
}

interface TableHeaderProps<RowDataType extends RowData> {
  headerGroups: HeaderGroup<RowDataType>[];
}

export function TableHeader<RowDataType extends RowData>({
  headerGroups,
}: TableHeaderProps<RowDataType>) {
  return (
    <ShadcnTable.TableHeader
      style={{
        display: "grid",
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      {headerGroups.map((headerGroup) => (
        <ShadcnTable.TableRow
          key={headerGroup.id}
          style={{ display: "flex", width: "100%" }}
        >
          {headerGroup.headers.map((header) => (
            <TableHead key={header.id} {...header} />
          ))}
        </ShadcnTable.TableRow>
      ))}
    </ShadcnTable.TableHeader>
  );
}
